define(function (require, exports, module) {
    'use strict';

    const { toNDigits } = require('lib/bytes');
    const { extInfo } = require('lib/extInfo');
    const icons = require('Cloud/ui/icons');
    const thumbnailer = require('./tools/cloud/thumbnailer');
    const MicroEvent = require('mrg-microevent');
    const UploadDescriptor = require('./UploadDescriptor');

    const { updateUploadFilesAction } = require('reactApp/modules/uploadList/uploadList.module');
    const { store: reduxStore } = require('reactApp/store');

    /**
     * @constructor
     * @extends {MicroEvent}
     * @param {UploadEntryModel.List} queue
     */
    function UploadDataSource(queue) {
        MicroEvent.call(this);

        this._queue = queue;
        queue.on('change', this);
        queue.on('remove', this);
    }

    Object.assign(UploadDataSource, {
        EVENT_CHANGE: 'change',
        EVENT_STATUS: 'status',
        EVENT_REMOVE: 'remove',
    });

    UploadDataSource.prototype = Object.assign(Object.create(MicroEvent.prototype), {
        constructor: UploadDataSource,

        /**
         * @param {UploadDescriptor} descriptor
         * @returns {string}
         */
        _getThumbnail: function (descriptor) {
            const extension = descriptor.get('nameParts').extension;
            const extensionData = extInfo.get(extension);
            const kind = extensionData.kind;
            const hash = descriptor.get('cloudHash');
            const size = descriptor.get('size');

            return thumbnailer.getThumbnail(extension, kind, hash, size);
        },

        /**
         * @param {UploadDescriptor} descriptor
         * @returns {Object}
         */
        _getItemData: function (descriptor) {
            // TODO: isVirus
            const state = descriptor.get('state');
            const nameParts = descriptor.get('nameParts');
            const isDirectory = descriptor.get('isDirectory');

            const data = {
                id: descriptor.get('id'),
                nameParts,
                state,
                isPublicUpload: !!descriptor.get('isPublicUpload'),
            };

            data.publishStatus = descriptor.get('publishStatus');

            if (isDirectory) {
                data.icon = icons.getFolderIcon('folder');
            } else {
                const size = descriptor.get('size');
                const extension = nameParts.extension;

                data.icon = icons.getFileIcon(extension);

                if (size !== -1) {
                    let sizeString = descriptor.meta('size');

                    if (sizeString) {
                        data.size = sizeString;
                    } else {
                        sizeString = toNDigits(size, 3).value;
                        data.size = sizeString;
                        descriptor.meta('size', sizeString);
                    }

                    if (extension && state === UploadDescriptor.STATE_DONE) {
                        data.thumbnail = this._getThumbnail(descriptor);
                    }

                    if (state === UploadDescriptor.STATE_UPLOADING) {
                        data.progress = descriptor.get('progress');
                    }
                }
            }

            const error = descriptor.get('error');

            if (error) {
                data.error = error.toString();
            }

            return data;
        },

        /**
         * @returns {Array.<Object>}
         */
        _getItemsData: function () {
            const data = this._queue && this._queue.map(this._getItemData, this);

            return data;
        },

        /**
         * @param {Emitter.Event} event
         * @param {UploadDescriptor} descriptor
         */
        _onChange: function (event, descriptor) {
            const data = this._getItemData(descriptor);

            this.emit(UploadDataSource.EVENT_CHANGE, data);

            const hasStateChange = event.changes.includes('state');

            if (hasStateChange) {
                const hasFinishedState = descriptor.hasFinishedState();

                if (hasFinishedState) {
                    const status = this.getStatus();

                    if (data.state === UploadDescriptor.STATE_DONE) {
                        reduxStore.dispatch(
                            updateUploadFilesAction({
                                descriptorId: descriptor.get('id'),
                                cloudPath: descriptor.get('cloudPath'),
                                thumb: this._getThumbnail(descriptor),
                            })
                        );
                    }

                    this.emit(UploadDataSource.EVENT_STATUS, status);
                }
            }
        },

        _onRemove: function (event, descriptor) {
            this.emit(UploadDataSource.EVENT_REMOVE, descriptor);
        },

        /**
         * @param {Emitter.Event} event
         * @param {UploadDescriptor} descriptor
         */
        handleEvent: function (event, descriptor) {
            if (event.type === 'change') {
                this._onChange(event, descriptor);
            }

            if (event.type === 'remove') {
                this._onRemove(event, descriptor);
            }
        },

        /**
         * @returns {Promise}
         * @resolves {Array.<Object>}
         * @rejects {Error}
         */
        load: function () {
            const data = this._getItemsData();

            return Promise.resolve(data);
        },

        /**
         * @returns {Promise}
         * @resolves {Array.<Object>}
         * @rejects {Error}
         */
        loadNext: function () {
            return Promise.reject(new Error('nothing'));
        },

        /**
         * @returns {boolean}
         */
        hasMore: function () {
            return false;
        },

        /**
         * @returns {Object} – {total, inProgress, finished, uploaded, failed, canceled}
         */
        getStatus: function () {
            // TODO: optimize
            const queue = this._queue;
            const total = queue.length;
            const data = {
                progress: 0,
                inProgress: 0,
                finished: 0,
                canceled: 0,
                uploaded: 0,
                failed: 0,
                total,
            };

            for (let i = 0; i < total; i++) {
                const descriptor = queue[i];
                const state = descriptor.get('state');

                if (state === UploadDescriptor.STATE_DONE) {
                    data.uploaded++;
                } else if (state === UploadDescriptor.STATE_FAIL) {
                    data.failed++;
                } else if (state === UploadDescriptor.STATE_CANCEL) {
                    data.canceled++;
                } else {
                    data.inProgress++;
                }
            }

            data.finished = data.total - data.inProgress;
            data.progress = Math.round((data.finished * 100) / data.total);

            return data;
        },

        checkStatus: function () {
            const status = this.getStatus();

            this.emit(UploadDataSource.EVENT_STATUS, status);
        },

        destroy: function () {
            this._queue.off('change', this);
            this._queue = null;
        },
    });

    module.exports = UploadDataSource;
});
